"use client";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useState,
} from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import Link from "next/link";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "../ui/input-otp";
import { Typography } from "../ui/typography";
import useAuth from "@/hooks/use-auth";
import { useAuthDialogStore } from "@/hooks/use-auth-dialog";

const formSchema = z
  .object({
    email: z.string(),
    phone: z.string().min(8, {
      message: "Утасны дугаар буруу",
    }),
    password: z.string().min(8, {
      message: "Нууц үг 8 оронтой байх ёстой",
    }),
    repeat: z.string().min(8, {
      message: "Нууц үг таарахгүй байна",
    }),
    otp: z.string().optional(),
  })
  .refine((data) => data.password === data.repeat, {
    path: ["repeat"],
    message: "Passwords must match.",
  });

const FormSignUp = ({
  setShowOtp,
  showOtp,
}: {
  setShowOtp: Dispatch<SetStateAction<boolean>>;
  showOtp: boolean;
}) => {
  const searchParams = useSearchParams();
  const { push } = useRouter();
  const { signUpUser, confirmSignUpUser, autoSignInUser, getCurrentUserInfo, resendEmailSignUpCode, } = useAuth();
  const { signUp } =
      useAuthDialogStore();
  const [loading, setLoading] = useState(false);
  const [isTermsAndPolicyAccepted, setIsTermsAndPolicyAccepted] =
    useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: searchParams.get("email") || "",
      phone: "",
      password: "",
      repeat: "",
      otp: "", // or undefined, since it's optional
    },    
  });

  const onSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      setLoading(true);
      try {
        if (!showOtp) {
          // Step 1: Sign up the user
          const response = await signUpUser(values.email, `+976${values.phone}`, values.password);
          // console.log("Sign up response", response);
          toast.success("Verification code sent to your email.");
          setShowOtp(true);
        } else {
          // Step 2: Confirm the OTP
          const confirmResponse = await confirmSignUpUser(values.email, values.otp || "");
          // console.log("Confirm sign up response", confirmResponse);
          await autoSignInUser();
          const currentUserInfo = await getCurrentUserInfo();
          // console.log("Current user info : ", currentUserInfo)
          toast.success("Account confirmed! Redirecting...");
          setShowOtp(false); // reset OTP screen
          signUp.closeDialog();
          // push("/login"); // or wherever you want
        }
      } catch (error: any) {
        toast("Error", {
          description: error.message,
        });
      } finally {
        setLoading(false);
      }
    },
    [showOtp, push]
  ); 

  const handleResendEmailSignUpCode = useCallback(async () => {
    const email = form.getValues("email");
  
    if (!email) {
      toast.error("Email is required to resend the code.");
      return;
    }
  
    try {
      await resendEmailSignUpCode(email);
      toast.success("Verification code resent to your email.");
    } catch (error: any) {
      toast.error("Failed to resend verification code", {
        description: error.message || "Something went wrong.",
      });
    }
  }, [form, resendEmailSignUpCode]);
  

  const handleShowPassword = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-7">
        {showOtp && (
          <>
            <Typography className="font-semibold">
              Security verification Email
            </Typography>
            <Typography className="font-semibold">
              To secure your account, please complete the following
              verification. We have send verification code to
              <span className="text-primary pl-1">{form.watch("email")}</span>
            </Typography>
            <FormField
              control={form.control}
              name="otp"
              render={({ field }) => (
                <FormItem>
                  <div className="flex justify-between">
                    <FormLabel>Verification code</FormLabel>
                  </div>
                  <FormControl>
                    <InputOTP
                      maxLength={6}
                      {...field}
                      onChange={(value) => {
                        // Only allow numeric values
                        const numericValue = value.replace(/\D/g, "");
                        field.onChange(numericValue);
                      }}
                      className="w-full"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot
                          className=" text-xl sm:h-14 sm:w-14 sm:text-2xl md:h-18 md:w-18 md:text-3xl"
                          index={0}
                        />
                      </InputOTPGroup>
                      <InputOTPGroup>
                        <InputOTPSlot
                          className=" text-xl sm:h-14 sm:w-14 sm:text-2xl md:h-18 md:w-18 md:text-3xl"
                          index={1}
                        />
                      </InputOTPGroup>
                      <InputOTPGroup>
                        <InputOTPSlot
                          className=" text-xl sm:h-14 sm:w-14 sm:text-2xl md:h-18 md:w-18 md:text-3xl"
                          index={2}
                        />
                      </InputOTPGroup>
                      <InputOTPSeparator />
                      <InputOTPGroup>
                        <InputOTPSlot
                          className=" text-xl sm:h-14 sm:w-14 sm:text-2xl md:h-18 md:w-18 md:text-3xl"
                          index={3}
                        />
                      </InputOTPGroup>
                      <InputOTPGroup>
                        <InputOTPSlot
                          className=" text-xl sm:h-14 sm:w-14 sm:text-2xl md:h-18 md:w-18 md:text-3xl"
                          index={4}
                        />
                      </InputOTPGroup>
                      <InputOTPGroup>
                        <InputOTPSlot
                          className=" text-xl sm:h-14 sm:w-14 sm:text-2xl md:h-18 md:w-18 md:text-3xl"
                          index={5}
                        />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                  <div>
                    Didn’t get a code?{" "}
                    <span className="font-semibold cursor-pointer" onClick={handleResendEmailSignUpCode}>
                      Click to resend
                    </span>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex gap-3">
              <Button
                type="button"
                className="w-full text-lg font-semibold flex-1"
                variant={"outline"}
                onClick={() => setShowOtp(false)}
              >
                Back
              </Button>
              <Button
                type="submit"
                className="w-full text-lg font-semibold flex-1"
                disabled={loading || !isTermsAndPolicyAccepted}
              >
                Continue
              </Button>
            </div>
          </>
        )}
        {!showOtp && (
          <>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Имэйл хаяг</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="your@example.com"
                      {...field}
                      type="email"
                      required
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Утасны дугаар</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Утасны дугаар"
                      {...field}
                      type="number"
                      required
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Нууц үг</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="********"
                        {...field}
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-passwords"
                      />
                      <div
                        className="absolute right-2 top-2 cursor-pointer"
                        onClick={handleShowPassword}
                      >
                        {showPassword ? (
                          <EyeOffIcon size={20} />
                        ) : (
                          <EyeIcon size={20} />
                        )}
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="repeat"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Нууц үг давтах</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="********"
                        {...field}
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        required
                      />
                      <div
                        className="absolute right-2 top-2 cursor-pointer"
                        onClick={handleShowPassword}
                      >
                        {showPassword ? (
                          <EyeOffIcon size={20} />
                        ) : (
                          <EyeIcon size={20} />
                        )}
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex cursor-pointer items-center gap-3">
              <Checkbox
                id="remember"
                checked={isTermsAndPolicyAccepted}
                onCheckedChange={(val: boolean) =>
                  setIsTermsAndPolicyAccepted(val)
                }
              />
              <Label
                htmlFor="remember"
                className="text-base font-normal text-nowrap flex flex-wrap"
              >
                By continuing, you agree to our{" "}
                <Link
                  href="/terms/terms.pdf"
                  target="_blank"
                  className="cursor-pointer text-blue-700 underline text-nowrap"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  href="/terms/privacy.pdf"
                  target="_blank"
                  className="cursor-pointer text-blue-700 underline text-nowrap"
                >
                  Privacy Policy
                </Link>
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full text-lg font-semibold"
              variant={"default"}
              disabled={loading || !isTermsAndPolicyAccepted}
            >
              Continue
            </Button>
          </>
        )}
      </form>
    </Form>
  );
};

export default FormSignUp;
